import { LogGroup, LogGroupOptions } from '@mbrtargeting/metatag-shared-types';
import { getUppermostReachableWindow, ReplaySubject } from '@mbrtargeting/metatag-utils';

/**
 * The different log levels.
 */
export enum LogLevel {
    DEBUG = 10,
    INFO = 20,
    NOTICE = 30,
    DEPRECATED = 35,
    WARNING = 40,
    ERROR = 50,
    CRITICAL = 60,
    ALERT = 70,
    EMERGENCY = 80
}

/**
 * The structure of a log entry.
 */
export interface LogEntry extends LogGroupOptions {
    date: Date;
    logLevel: LogLevel;
    message: string;
    messageObjects?: any[];
    stack?: string;
}

/**
 * The subject where all log entries are stored internally.
 *
 * Lookup existing logEntries$ via Symbol from the uppermost reachable window or creates a new ReplaySubject.
 */
export const logEntries$: ReplaySubject<LogEntry> = getUppermostReachableWindow<any>()[Symbol.for('http://stroeerdev.de/logger')] ||= new ReplaySubject<LogEntry>(1e6);

/**
 * Creates a log group with a given prefix.
 *
 * @param param0 The options for the log group.
 * @returns The log helper functions.
 */
export const logGroup = <OPTIONS extends LogGroupOptions>(options: OPTIONS): LogGroup<OPTIONS> => {
    const { prefix } = options;
    // logger factory function
    const logFn = (logLevel: LogLevel) => (message: string, messageObjects?: any[]) => logEntries$.next({
        ...options,
        date: new Date(),
        logLevel,
        message: prefix ? `${prefix}: ${message}` : message,
        messageObjects,
        stack: new Error().stack,
    });
    // returning log helper functions
    return {
        logGroup: (options2: Partial<OPTIONS>) => logGroup({ ...options, ...options2 }),
        debug: logFn(LogLevel.DEBUG),
        info: logFn(LogLevel.INFO),
        notice: logFn(LogLevel.NOTICE),
        deprecated: logFn(LogLevel.DEPRECATED),
        warn: logFn(LogLevel.WARNING),
        error: logFn(LogLevel.ERROR),
        critical: logFn(LogLevel.CRITICAL),
        alert: logFn(LogLevel.ALERT),
        fatal: logFn(LogLevel.EMERGENCY),
    }
}
